import { ReactNode, memo } from 'react'
import { useDrag } from 'react-dnd'
import { Trans } from 'react-i18next'

import { Nullable } from '@types'

import {
  Shortlist,
  ShortlistCategoriesConfigProps,
  ShortlistExtendedProfile,
  ShortlistTableConfigProps,
} from '@modules/shortlist/types'

import { HighlightedRowLabel, StyledTableCell, StyledTableRow, TableCellInner } from './index.styles'

export interface DraggableTableRowProps {
  tableColumnsConfig: ShortlistTableConfigProps[]
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  tableColumnsComponents: Record<ShortlistTableConfigProps['fieldName'], () => ReactNode>
  profileId: number
  profileUpdatedDate: string
  profileData: ShortlistExtendedProfile
  isActive: boolean
  isHighlighted?: boolean
  highlightedRowLabel?: string
  activeShortlist?: Shortlist
}

const DraggableTableRow = ({
  tableColumnsConfig,
  optionalColumnsConfig,
  tableColumnsComponents,
  profileId,
  profileUpdatedDate,
  profileData,
  isActive,
  isHighlighted,
  highlightedRowLabel,
  activeShortlist,
}: DraggableTableRowProps) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'profile',
    item: {
      profileId,
      renderer: tableColumnsComponents[tableColumnsConfig.find(config => config.isDragHandle)?.fieldName || ''],
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  return (
    <StyledTableRow key={profileId + profileUpdatedDate} $isActive={isActive} $isHighlighted={isHighlighted}>
      {tableColumnsConfig.map((tableColumn: ShortlistTableConfigProps, idx: number) => {
        const { restCellProps, fieldName, isEnabled, isDragHandle } = tableColumn

        const customField = activeShortlist?.customFields?.find(field => field?.fieldName === tableColumn?.fieldName)

        if (
          !(
            isEnabled ||
            !!customField ||
            (optionalColumnsConfig && optionalColumnsConfig[fieldName as keyof ShortlistCategoriesConfigProps]?.visible)
          )
        )
          return null

        const cellComponent = tableColumnsComponents[fieldName]
        const extraStyles =
          (isDragging && isDragHandle && { cursor: 'grabbing' }) || (isDragHandle && { cursor: 'pointer' }) || {}

        return (
          <StyledTableCell
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...restCellProps}
            key={profileId + fieldName + profileUpdatedDate}
            data-testid={`${fieldName}_testid`}
            $isDisabled={!isActive && !tableColumn.customDisable}
            $isHighlighted={isHighlighted}
          >
            <TableCellInner
              style={extraStyles}
              data-testid={`${fieldName}_testid_inner`}
              draggable={isDragHandle}
              ref={isDragHandle ? drag : null}
            >
              {cellComponent ? cellComponent() : (profileData[fieldName as keyof ShortlistExtendedProfile] as string)}
            </TableCellInner>
            {idx === 0 && isHighlighted && highlightedRowLabel ? (
              <HighlightedRowLabel>
                <Trans>{highlightedRowLabel}</Trans>
              </HighlightedRowLabel>
            ) : null}
          </StyledTableCell>
        )
      })}
    </StyledTableRow>
  )
}

export default memo(DraggableTableRow)
